import React from 'react';
import useVehicleScheduleStore from '../stores/useVehicleScheduleStore';
import { useThemeStore } from '../stores/useThemeStore';
import { DAY_W, BAND_GAP, INFO_W, ROW_H } from './grid-constants';
import { DerivedVehicleBand, buildBandRuns } from './band-runs';
import { VehicleScheduleEvent } from './types';

/** dataTransfer key + payload for dragging a band BODY onto another vehicle row.
 *  Read on drop; the dragover highlight uses the store copy instead, because the
 *  spec forbids reading dataTransfer during dragover. */
export const DRAG_BAND_MIME = 'application/x-firefleet-band-move';

export interface BandContextMenuArgs {
  kind: 'vehicle' | 'rental';
  assignmentId: string;
  eventId: string;
  vehicleId: string;
  eventName: string;
  x: number;
  y: number;
}

interface VehicleBandProps {
  vehicleId: string;
  band: DerivedVehicleBand;
  /** dateStr → day index within the visible window. */
  dayIdx: Map<string, number>;
  windowStart: string;
  windowEnd: string;
  numDays: number;
  vehicleHue: number;
  /** Producer: open the leg editor + the right-click menu (any vehicle status). */
  canEdit: boolean;
  /** Producer AND a usable vehicle: drag the outer edges to set ship dates. */
  canDrag: boolean;
  /** Sub-lane index + total sub-lanes in this row (overlapping bands stack). */
  subLane: number;
  laneCount: number;
  onBandClick: (assignmentId: string) => void;
  onBandContextMenu: (args: BandContextMenuArgs) => void;
  onHover: (event: VehicleScheduleEvent, x: number, y: number, box?: { top: number; bottom: number }) => void;
  onHoverEnd: () => void;
}

/** Blue, reserved for the tour outline — distinguishes a tour from one-off shows.
 *  Day mode uses a deeper blue-600 so the frame stays crisp against white. */
const TOUR_ACCENT = 'rgba(96,165,250,0.95)';
const TOUR_ACCENT_DAY = 'rgba(37,99,235,0.9)';
const TOUR_BG = 'rgba(96,165,250,0.07)';
const TOUR_BG_DAY = 'rgba(37,99,235,0.06)';

/** Show runs get the solid vehicle fill; transit (leg) runs get a lighter, faded
 *  fill so the drive days read as connective tissue, not another show. In day mode
 *  the hues deepen a touch so the bars keep their "pop" against a white grid
 *  (dark mode: light-on-dark; day mode: a saturated bar with dark text on white). */
function fill(hue: number, leg: boolean, day: boolean): React.CSSProperties {
  if (day) {
    return leg
      ? { background: `hsl(${hue} 34% 66%)`, color: '#1c2230', opacity: 0.95 }
      : { background: `hsl(${hue} 52% 55%)`, color: '#10131a', opacity: 1 };
  }
  return leg
    ? { background: `hsl(${hue} 30% 74%)`, color: '#1c2230', opacity: 0.85 }
    : { background: `hsl(${hue} 47% 61%)`, color: '#10131a', opacity: 1 };
}

/**
 * One band = a standalone show or a whole TOUR rendered as a single connected run
 * of segments (leave → drive → show → drive-between → show → drive → home). A tour
 * gets an amber outline framing the full span so it's visually distinct from
 * one-off events. Dragging the outer edges edits the first event's ship_out /
 * last event's ship_back; right-click opens a menu.
 */
const VehicleBand: React.FC<VehicleBandProps> = ({
  vehicleId, band, dayIdx, windowStart, windowEnd, numDays, vehicleHue, subLane, laneCount, canEdit, canDrag, onBandClick, onBandContextMenu, onHover, onHoverEnd,
}) => {
  const { events, assignmentByEventId, isTour, tourName } = band;
  const dayMode = useThemeStore(s => s.dayMode);
  // Vertical geometry: one sub-lane fills the row (familiar look); 2+ split it so
  // overlapping bands stack and stay fully visible instead of muddying together.
  const subLaneH = laneCount > 1 ? ROW_H / laneCount : ROW_H;
  const pad = laneCount > 1 ? 3 : 8;
  const bandTop = subLane * subLaneH + pad;
  const bandH = subLaneH - pad * 2;
  const startDrag = useVehicleScheduleStore(s => s.startDrag);
  const startBandDrag = useVehicleScheduleStore(s => s.startBandDrag);
  const endBandDrag = useVehicleScheduleStore(s => s.endBandDrag);
  // Live drag for THIS band — the drag targets the first or last event's edge.
  const drag = useVehicleScheduleStore(s =>
    s.drag && s.drag.vehicleId === vehicleId &&
    (s.drag.eventId === band.firstEventId || s.drag.eventId === band.lastEventId) ? s.drag : null
  );
  const dragActive = useVehicleScheduleStore(s => !!s.drag);

  // Effective leave/return, shifted by the live drag preview.
  let effLeave = band.baseLeave;
  let effReturn = band.baseReturn;
  if (drag && !drag.invalid) {
    if (drag.edge === 'left') effLeave = drag.previewDate;
    else effReturn = drag.previewDate;
    if (effReturn < effLeave) { effLeave = effReturn = drag.previewDate; }
  }

  const runs = buildBandRuns(events, assignmentByEventId, effLeave, effReturn);
  if (!runs.length) return null;

  const clampIdx = (d: string): number => {
    if (d < windowStart) return 0;
    if (d > windowEnd) return numDays - 1;
    return dayIdx.get(d) ?? 0;
  };

  const leaveIdx = clampIdx(effLeave);
  const returnIdx = clampIdx(effReturn);

  const eventName = (eventId: string) => events.find(e => e.id === eventId)?.name ?? 'event';

  /**
   * The marker that says this show ALSO has transport off our fleet, so the truck
   * band is not the whole story. Without it the two bands for one show carry the
   * same bare name in two rows and read as a duplicate rather than as two
   * resources. Only the both-case pays the label width; a truck-only show, which
   * is most of them, is untouched.
   */
  const alsoExternal = (eventId: string): string => {
    const e = events.find(ev => ev.id === eventId);
    if (!e?.transport_arranged) return '';
    return e.transport_mode === 'driving' ? ' + rental' : ' + shipping';
  };
  // The marker's in-band form is a dot in the LANE's color (teal freight, sand
  // rental: the same hues the Shipping / Rentals bands wear), pinned BEFORE the
  // name and outside the truncating span. A one-day show run is ~52px of text,
  // which the name alone already overflows, so a trailing "+ shipping" never
  // painted; the tooltip and aria-label keep the words.
  const externalDot = (eventId: string): React.CSSProperties | null => {
    const e = events.find(ev => ev.id === eventId);
    if (!e?.transport_arranged) return null;
    const h = e.transport_mode === 'driving' ? 30 : 190;
    return { background: dayMode ? `hsl(${h} 40% 45%)` : `hsl(${h} 45% 70%)` };
  };

  const handleClick = (assignmentId: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (assignmentId) onBandClick(assignmentId);
  };

  const handleContextMenu = (assignmentId: string, eventId: string) => (e: React.MouseEvent) => {
    if (!canEdit || !assignmentId) return;
    e.preventDefault();
    e.stopPropagation();
    onBandContextMenu({ kind: 'vehicle', assignmentId, eventId, vehicleId, eventName: eventId ? eventName(eventId) : 'event', x: e.clientX, y: e.clientY });
  };

  // Drag the band BODY to another vehicle row → move the show there.
  //
  // A TOUR band moves as a unit: its transit is one continuous run, and item 2
  // established that a tour's vehicle is the one on EVERY member show. Moving a
  // single show off a tour's truck would quietly break that, so the scope is the
  // tour and the SERVER resolves which shows that means — the grid only knows its
  // visible window, and a tour can run past both edges of it.
  const dragScope: 'event' | 'tour' = isTour ? 'tour' : 'event';

  const handleBandDragStart = (eventId: string) => (e: React.DragEvent) => {
    const payload = { eventId, fromVehicleId: vehicleId, scope: dragScope };
    e.dataTransfer.setData(DRAG_BAND_MIME, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'move';
    startBandDrag({
      ...payload,
      label: isTour && tourName ? tourName : eventName(eventId),
      windowStart: effLeave,
      windowEnd: effReturn,
    });
  };

  const handlePointerDown = (edge: 'left' | 'right') => (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    startDrag({
      vehicleId,
      eventId: edge === 'left' ? band.firstEventId : band.lastEventId,
      edge,
      evStart: band.firstEventDate,
      evEnd: band.lastEventEnd,
      anchorDate: edge === 'left' ? band.baseReturn : band.baseLeave,
      previewDate: edge === 'left' ? band.baseLeave : band.baseReturn,
      invalid: false,
    });
  };

  return (
    <div className={`group/band ${dragActive ? 'pointer-events-none' : ''}`}>
      {/* Tour outline — frames the whole connected span so tours read distinctly
          from one-off shows. */}
      {isTour && returnIdx >= leaveIdx && (
        <div
          className="absolute rounded-lg pointer-events-none"
          style={{
            top: bandTop - 1,
            height: bandH + 2,
            left: leaveIdx * DAY_W + 1,
            width: (returnIdx - leaveIdx + 1) * DAY_W - 2,
            border: `1.5px solid ${dayMode ? TOUR_ACCENT_DAY : TOUR_ACCENT}`,
            background: dayMode ? TOUR_BG_DAY : TOUR_BG,
          }}
        />
      )}

      {/* Conflict outline — this band's window overlaps another on the SAME vehicle
          (one vehicle can't be two places at once). Resolve by combining into a tour
          or moving one show. */}
      {band.conflict && returnIdx >= leaveIdx && (
        <div
          className="absolute rounded-lg pointer-events-none"
          style={{
            top: bandTop - 1,
            height: bandH + 2,
            left: leaveIdx * DAY_W + 1,
            width: (returnIdx - leaveIdx + 1) * DAY_W - 2,
            border: `1.5px solid ${dayMode ? 'rgba(220,38,38,0.85)' : 'rgba(248,113,113,0.9)'}`,
            background: dayMode ? 'rgba(220,38,38,0.07)' : 'rgba(248,113,113,0.12)',
          }}
        />
      )}

      {runs.map((run) => {
        const sIdx = clampIdx(run.start);
        const eIdx = clampIdx(run.end);
        if (eIdx < sIdx) return null;
        const left = sIdx * DAY_W + BAND_GAP;
        const width = (eIdx - sIdx + 1) * DAY_W - BAND_GAP * 2;
        const style: React.CSSProperties = { ...fill(vehicleHue, run.leg, dayMode), left, width, top: bandTop, height: bandH };

        const dot = run.leg ? null : externalDot(run.eventId);
        return (
          <div
            key={`${run.kind}-${run.eventId}-${run.start}`}
            data-event-id={run.eventId}
            draggable={canDrag && !!run.eventId}
            onDragStart={canDrag && run.eventId ? handleBandDragStart(run.eventId) : undefined}
            onDragEnd={canDrag ? () => endBandDrag() : undefined}
            onClick={handleClick(run.assignmentId)}
            onContextMenu={handleContextMenu(run.assignmentId, run.eventId)}
            onMouseEnter={(e) => { const re = events.find(ev => ev.id === run.eventId); if (re) { const r = e.currentTarget.getBoundingClientRect(); onHover(re, e.clientX, e.clientY, { top: r.top, bottom: r.bottom }); } }}
            onMouseLeave={onHoverEnd}
            aria-label={`${run.label}${run.leg ? '' : alsoExternal(run.eventId)}. Click to edit drive legs, right-click for options.${canDrag ? ' Drag onto another vehicle row to move it there.' : ''}`}
            className={`absolute px-1 flex items-center justify-start ${canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${run.leg ? 'rounded' : 'rounded-md'}`}
            style={style}
            title={`${run.label}${run.leg ? '' : alsoExternal(run.eventId)}${tourName ? `  ·  tour: ${tourName}` : ''}${canDrag ? `\nDrag onto another vehicle row to move ${isTour ? 'the tour' : 'this show'}` : ''}`}
          >
            <span
              className="inline-flex items-center gap-1 min-w-0"
              style={{ position: 'sticky', left: INFO_W + BAND_GAP + 4, maxWidth: Math.max(0, width - 8) }}
            >
              {dot && <span aria-hidden className="w-1.5 h-1.5 rounded-full shrink-0 ring-1 ring-black/20" style={dot} />}
              <span className={`text-[11px] leading-tight truncate ${run.leg ? 'italic font-medium' : 'font-bold'}`}>
                {run.label}
              </span>
            </span>
          </div>
        );
      })}

      {/* Drag-extend handles — left edits first event's ship_out, right the last
          event's ship_back. */}
      {canDrag && (
        <>
          <div
            role="slider"
            aria-label="Drag to set leave date"
            onPointerDown={handlePointerDown('left')}
            className="absolute w-1.5 cursor-ew-resize opacity-0 group-hover/band:opacity-100 bg-primary rounded-l transition-opacity"
            style={{ left: leaveIdx * DAY_W + BAND_GAP, top: bandTop, height: bandH }}
          />
          <div
            role="slider"
            aria-label="Drag to set return date"
            onPointerDown={handlePointerDown('right')}
            className="absolute w-1.5 cursor-ew-resize opacity-0 group-hover/band:opacity-100 bg-primary rounded-r transition-opacity"
            style={{ left: (returnIdx + 1) * DAY_W - BAND_GAP - 6, top: bandTop, height: bandH }}
          />
        </>
      )}
    </div>
  );
};

export default VehicleBand;
